import type { ApiResponse } from '../types/api'
import { api } from './api-client'

// The FunSports member token, minted here and handed to the lite app.
//
// The app cannot mint one: this endpoint takes the member's password, and the app never sees it —
// it hosts this page in a WebView and the login happens in here. So the page mints and pushes.
//
// Everything below is inert in a browser. Without the native bridge there is nobody to push to,
// so no request is made at all and web users pay nothing for this.

const MEMBER_TOKEN_PATH = '/api/funsports/member-token'

// Renew this far ahead of expiry, so a request never goes out on a token about to die.
const SAFETY_MARGIN_MS = 60 * 1000
// Used when the reply carries no lifetime and the token has no readable `exp`.
const FALLBACK_TTL_MS = 30 * 60 * 1000
// Never sit on a timer longer than this: a tab left open for a day should still re-check.
const MAX_REFRESH_MS = 6 * 60 * 60 * 1000
// After a failed mint, try again on this cadence rather than giving up until the next login.
const RETRY_MS = 60 * 1000
// Floor for any scheduled mint. A token whose life is at or under the safety margin computes a
// non-positive delay, and without this each answer would immediately trigger the next one — a
// rapid loop of authenticated requests rather than a refresh.
const MIN_REFRESH_MS = 30 * 1000

interface MemberToken {
  token: string
  expiresIn: number
  baseUrl?: string
}

// Why a mint produced no token. The difference decides whether to retry: a signed-out member never
// will, and treating a transient server error the same way strands the app on its guest ticket
// until the next login.
export type MintOutcome =
  | { kind: 'token'; token: MemberToken }
  | { kind: 'signed-out' }
  | { kind: 'failed' }

// Codes that mean "not this member", as opposed to "not right now". They arrive two ways: inside
// the envelope, and as an HTTP status the api-client rejects on before any envelope is parsed.
const SIGNED_OUT_CODES = new Set([401, 403])

// The HTTP status behind a rejection, if there is one.
//
// `handleFetchError` wraps the AxiosError in a FetchClientError, so the status sits one level
// further in than it looks; a network failure has no status at all and must stay retryable.
const httpStatusOf = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null) return undefined
  const wrapper = error as {
    response?: { status?: unknown; response?: { status?: unknown } }
  }
  const status = wrapper.response?.response?.status ?? wrapper.response?.status
  return typeof status === 'number' ? status : undefined
}

// The bridge is only present in the lite app, and only on builds new enough to accept a token.
const bridge = () =>
  typeof window === 'undefined' ? undefined : window.Fun88Native

export const supportsSportsToken = (): boolean =>
  typeof bridge()?.setSportsToken === 'function'

// This backend answers in two shapes: the `/api/funsports/*` routes use {code, data} while the
// PHP gateway uses {status, info}. Reading both costs three lines and removes a class of
// "works on staging" bug.
const readEnvelope = (
  body: unknown,
): { payload: Record<string, unknown> | null; signedOut: boolean } => {
  if (typeof body !== 'object' || body === null) {
    return { payload: null, signedOut: false }
  }
  const envelope = body as { code?: unknown; data?: unknown } & ApiResponse<unknown>
  if (typeof envelope.code === 'number' && SIGNED_OUT_CODES.has(envelope.code)) {
    return { payload: null, signedOut: true }
  }
  if (envelope.code !== 200 && envelope.status !== 1) {
    // Anything else unsuccessful — a 500, a shape nobody expected — is worth another try.
    return { payload: null, signedOut: false }
  }
  const payload = envelope.data ?? envelope.info
  return {
    payload:
      typeof payload === 'object' && payload !== null
        ? (payload as Record<string, unknown>)
        : null,
    signedOut: false,
  }
}

// Seconds until the JWT's `exp`, or null. Not verified — only read, so the page knows when to
// renew rather than discovering it from a 401.
const expiresInFromJwt = (token: string): number | null => {
  try {
    const payload = token.split('.')[1]
    if (!payload) return null
    const padded = payload
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(Math.ceil(payload.length / 4) * 4, '=')
    const exp = (JSON.parse(atob(padded)) as { exp?: unknown }).exp
    if (typeof exp !== 'number' || !Number.isFinite(exp)) return null
    const seconds = Math.floor(exp - Date.now() / 1000)
    return seconds > 0 ? seconds : null
  } catch {
    return null
  }
}

export const fetchSportsMemberToken = async (): Promise<MintOutcome> => {
  // No body: the request interceptor adds the member's credentials and `auth`.
  const response = await api.post<ApiResponse<unknown>>(MEMBER_TOKEN_PATH, {})
  const { payload, signedOut } = readEnvelope(response.data)
  if (signedOut) return { kind: 'signed-out' }

  const token = payload?.token
  // A success envelope with no token in it is not a signed-out member; it is a reply we did not
  // understand, and the next attempt may well work.
  if (typeof token !== 'string' || !token) return { kind: 'failed' }

  const stated = Number(payload?.expiresIn)
  const expiresIn =
    Number.isFinite(stated) && stated > 0
      ? stated
      : (expiresInFromJwt(token) ?? FALLBACK_TTL_MS / 1000)

  const baseUrl = payload?.baseUrl
  return {
    kind: 'token',
    token: {
      token,
      expiresIn,
      baseUrl: typeof baseUrl === 'string' && baseUrl ? baseUrl : undefined,
    },
  }
}

let timer: ReturnType<typeof setTimeout> | undefined
// Guards against a second run while one is in flight — the hook re-fires on any store change.
let running = false
// Bumped by every start and stop. A mint captures it and discards its own answer if it changed
// while in flight: signing out mid-request would otherwise push the departed member's token back
// to the app, leaving the native sportsbook authenticated as the wrong person.
let run = 0

const clearTimer = () => {
  if (timer !== undefined) clearTimeout(timer)
  timer = undefined
}

const schedule = (ms: number) => {
  clearTimer()
  const delay = Math.min(Math.max(ms, MIN_REFRESH_MS), MAX_REFRESH_MS)
  timer = setTimeout(() => void mintAndPush(), delay)
}

const mintAndPush = async (): Promise<void> => {
  if (!supportsSportsToken()) return
  const mine = run

  try {
    const outcome = await fetchSportsMemberToken()
    // Signed out, or started again, while this was in flight. Pushing now would hand the app a
    // token for whoever was signed in when it began.
    if (mine !== run) return

    switch (outcome.kind) {
      case 'token':
        bridge()?.setSportsToken?.(
          outcome.token.token,
          outcome.token.expiresIn,
          outcome.token.baseUrl,
        )
        schedule(outcome.token.expiresIn * 1000 - SAFETY_MARGIN_MS)
        return
      case 'signed-out':
        signedOut()
        return
      case 'failed':
        schedule(RETRY_MS)
        return
      default: {
        // Unreachable. `outcome` narrows to `never` here, so adding a variant without a case
        // above fails the build rather than falling through silently.
        const unhandled: never = outcome
        throw new Error(`unhandled mint outcome: ${String(unhandled)}`)
      }
    }
  } catch (error) {
    if (mine !== run) return

    // A 401 or 403 rejects before any envelope exists to classify, so it has to be recognised
    // here too. Retrying one every minute would never succeed, and would leave the app holding a
    // token for a member the server has stopped accepting.
    if (SIGNED_OUT_CODES.has(httpStatusOf(error) ?? 0)) {
      signedOut()
      return
    }

    // The app keeps browsing on its guest ticket meanwhile, so a failure here costs favourites
    // and the bet slip, not the page.
    schedule(RETRY_MS)
  }
}

/// Nothing to retry for. `running` is released so a later sign-in can start the cycle again.
const signedOut = (): void => {
  clearTimer()
  bridge()?.setSportsToken?.('', 0)
  running = false
}

/** Mint a token for the signed-in member and keep it fresh. No-op outside the lite app. */
export const startSportsMemberToken = async (): Promise<void> => {
  if (!supportsSportsToken() || running) return
  running = true
  run++
  await mintAndPush()
}

/** Stop refreshing and tell the app to drop what it has. Call on sign-out. */
export const stopSportsMemberToken = (): void => {
  clearTimer()
  running = false
  // Invalidates any mint still in flight, so its answer is dropped rather than pushed after the
  // member has gone.
  run++
  bridge()?.setSportsToken?.('', 0)
}
